<script setup>
import router from '@/router';
import {computed, onMounted, ref} from 'vue';
import {authenticateWithBiometric, getAvailableTypeString} from '@/services/BiometricService';
import {Capacitor} from '@capacitor/core';
import {BiometricAuth} from '@aparajita/capacitor-biometric-auth';
import {useI18n} from 'vue-i18n';
import {getApiBaseUrl, getBiometricLSKeyByDomain, showSuccessMessage} from '@/helper';
import CustomDropzone from "@/components/forms/CustomDropzone.vue";
import Button from "@/components/forms/Button.vue";
import SettingsService from "@/services/SettingsService";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import IntermediaryService from "@/services/IntermediaryService";
import {getLoggedUser} from "@/services/AuthService";
import Input from "@/components/forms/Input.vue";
import Select from "@/components/forms/Select.vue";
import FormTemplateService from "@/services/FormTemplateService";
import ContractTypeService from "@/services/ContractTypeService";
import AssistedOnboardingSettings from "@/components/settings/AssistedOnboardingSettings.vue";
import DocumentTemplateService from "@/services/DocumentTemplateService";

const {t} = useI18n()
const biometricActive = ref(false);
const biometricTypeAvailable = ref('');
const biometricInfo = ref(null);
const documentsLogoBase64 = ref(null);
const isAdmin = ref(false);

const onboardingOptions = ref({investment_questionnaires: [], aml_questionnaires: [], forms: [], meeting_records: [], identification: false, client_complex_form: false, address: false, bank_account: false, documents: false, calculation: false, strategies: false});
const assistedOnboardingOptions = ref([{}]);
const investmentQuestionnaires = ref([]);
const amlQuestionnaires = ref([]);
const forms = ref([]);
const meetingRecords = ref([]);
const createContractChoice = ref(false);
const contractTypes = ref([]);
const documentsTemplates = ref([]);

onMounted(async () => {
  getLoggedUser().then(response => {
    let loggedUser = response;
    if (loggedUser.role === 'ADMIN' || loggedUser.role === 'SUPERADMIN') {
      isAdmin.value = true;
    }
  });

  if (localStorage.getItem(getBiometricLSKeyByDomain('BIOMETRIC_ACTIVE')) === 'true') {
    biometricActive.value = true;
  }
  biometricInfo.value = await BiometricAuth.checkBiometry();
  biometricTypeAvailable.value = await getAvailableTypeString();
  documentsLogoBase64.value = await getSettingsValueByKey('documents_logo_base64');

  investmentQuestionnaires.value = await getFormsTemplatesByType('INVESTMENT_QUESTIONNAIRE');

  amlQuestionnaires.value = await getFormsTemplatesByType('AML_QUESTIONNAIRE');

  forms.value = await getFormsTemplatesByType('FORM');

  meetingRecords.value = await getFormsTemplatesByType('MEETING_RECORD');

  getContractTypes();

  const newOnboardingOptions = JSON.parse(await getSettingsValueByKey('self_onboarding_settings'))
  onboardingOptions.value = {...onboardingOptions.value, ...newOnboardingOptions};
  if (onboardingOptions.value.contract_type) {
    createContractChoice.value = true;
  }

  await getActiveDocumentsTemplates();
});

function activateBiometric() {
  biometricActive.value = true;
  localStorage.setItem(getBiometricLSKeyByDomain('BIOMETRIC_ACTIVE'), 'true');
  if (biometricActive.value === true) {
    authenticateWithBiometric(false);
  }
}

function deactivateBiometric() {
  biometricActive.value = false;
  localStorage.removeItem(getBiometricLSKeyByDomain('BIOMETRIC_ACTIVE'));
}

function getSettingsValueByKey(key) {
  return SettingsService.getSettingsValueByKey(key)
      .then((response) => {
        return response[key];
      })
      .catch((error) => {
      });
}

function onUploadSuccess(file, response) {
  getSettingsValueByKey('documents_logo_base64').then((base64) => {
    documentsLogoBase64.value = base64;
  });
}

function onUploadError() {
  console.log('error');
}

function deleteSettingsLogo() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('settings.delete_logo_for_documents'),
    message: t('settings.delete_logo_for_documents_confirm'),
  });
  onConfirm(() => {
    SettingsService.deleteSettingsDocumentsLogo().then(() => {
      documentsLogoBase64.value = null;
      showSuccessMessage(t('settings.logo_deleted'));
    });
  });
  reveal();
}

async function getFormsTemplatesByType(type) {
  const formTemplates = await FormTemplateService.getAllByType(type);
  return formTemplates;
}

async function getActiveDocumentsTemplates() {
  documentsTemplates.value = await DocumentTemplateService.getAllActive();
}

function getContractTypes() {
  ContractTypeService.getAll().then((response) => {
    contractTypes.value = response;
  });
}

function handleCreateContractChoice() {
  if (!createContractChoice.value) {
    onboardingOptions.value.contract_type = null;
  }
}

function saveSelfOnboardingSettings() {
  const body = {
    'key': 'self_onboarding_settings',
    'value': JSON.stringify(onboardingOptions.value)
  }
  saveOnboardingSettings(body);
}

function saveOnboardingSettings(body) {
  SettingsService.saveOnboardingSettings(body)
      .then(() => {
        showSuccessMessage(t('settings.self_onboarding_settings_saved'));
      })
      .catch((error) => {
        console.log(error);
      });
}

const selectedContractTypeName = computed(() => {
  const contractType = contractTypes.value.find(type => type.id === onboardingOptions.value.contract_type) || null;
  return contractType ? contractType.name : '';
})
</script>
<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-3">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('settings.back') }}</a>
        </div>
        <div class="col-6 text-center">
          <div class="page-section__title">
            {{ t('settings.settings') }}
          </div>
        </div>
      </div>
    </div>
    <div class="page-section__content">
      <div class="mb-4">
        <div v-if="Capacitor.isNativePlatform() && biometricInfo?.isAvailable" class="row mb-3">
          <div class="col-12">
            <div class="card">
              <div class="card-body p-4">
                <div class="row">
                  <div class="col d-flex align-items-center">
                    {{ t('settings.login_with') }} {{ biometricTypeAvailable }}
                  </div>
                  <div class="col-auto">
                    <button v-if="!biometricActive" @click="activateBiometric" class="btn btn-primary"> {{ t('settings.activate') }}</button>
                    <button v-if="biometricActive" @click="deactivateBiometric" class="btn btn-danger"> {{ t('settings.deactivate') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isAdmin" class="row mb-3">
          <div class="col-12">
            <div class="card mb-3">
              <div class="card-header">
                <div class="card-header__title">
                  {{ t('settings.selfOnboardingSettings') }}
                </div>
              </div>
              <form @submit.prevent="saveSelfOnboardingSettings">
                <div class="card-body p-4">
                  <div class="row mb-3">
                    <div class="col-12 form-group">
                      <label for="parentId" class="form-label">Investiční dotazník</label>
                      <select class="form-select" v-model="onboardingOptions.investment_questionnaires">
                        <option value="">---</option>
                        <option v-for="(investmentQuestionnaire, index) in investmentQuestionnaires" :key="index" :value="[investmentQuestionnaire.id]">{{ investmentQuestionnaire.name }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-12 form-group">
                      <label for="parentId" class="form-label">AML dotazník</label>
                      <select class="form-select" v-model="onboardingOptions.aml_questionnaires">
                        <option value="[]">---</option>
                        <option v-for="(amlQuestionnaire, index) in amlQuestionnaires" :key="index" :value="[amlQuestionnaire.id]">{{ amlQuestionnaire.name }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-12 form-group">
                      <label for="parentId" class="form-label">Formuláře</label>
                      <select class="form-select" v-model="onboardingOptions.forms" multiple>
                        <option value="">---</option>
                        <option v-for="(form, index) in forms" :key="index" :value="form.id">{{ form.name }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-12 form-group">
                      <label for="parentId" class="form-label">Záznam z jednání</label>
                      <select class="form-select" v-model="onboardingOptions.meeting_records">
                        <option value="[]">---</option>
                        <option v-for="(meetingRecord, index) in meetingRecords" :key="index" :value="[meetingRecord.id]">{{ meetingRecord.name }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="form-group">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchToIdentify" v-model="onboardingOptions.identification"/>
                        <label class="form-check-label" for="switchToIdentify">Provést identifikaci</label>
                      </div>
                    </div>
                    <div class="form-group">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchFillData" v-model="onboardingOptions.client_complex_form"/>
                        <label class="form-check-label" for="switchFillData">Vyplnění údajů</label>
                      </div>
                    </div>
                    <div class="form-group">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchFillAddress" v-model="onboardingOptions.address"/>
                        <label class="form-check-label" for="switchFillAddress">Adresa</label>
                      </div>
                    </div>
                    <div class="form-group">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchFillBankAccount" v-model="onboardingOptions.bank_account"/>
                        <label class="form-check-label" for="switchFillBankAccount">Bankovní účet</label>
                      </div>
                    </div>
                    <div class="form-group">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchDocuments" v-model="onboardingOptions.documents"/>
                        <label class="form-check-label" for="switchDocuments">Dokumenty</label>
                      </div>
                    </div>
                    <div class="form-group">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchCalculation" v-model="onboardingOptions.calculation"/>
                        <label class="form-check-label" for="switchCalculation">Kalkulačka</label>
                      </div>
                      <div v-if="onboardingOptions.calculation && onboardingOptions.contract_type" class="row mb-3">
                        <span>Klientovi budou strategie přiřazeny k vytvořené smlouvě: {{ selectedContractTypeName }}</span>
                      </div>
                      <div v-if="!onboardingOptions.contract_type && onboardingOptions.calculation" class="row mb-3">
                        <span>Je potřeba zvolit vytvoření smlouvy</span>
                      </div>
                    </div>
                    <div class="form-group">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchCreateContractChoice" v-model="createContractChoice" @change="handleCreateContractChoice"/>
                        <label class="form-check-label" for="switchCreateContractChoice">Vytvořit smlouvu</label>
                      </div>
                    </div>
                    <div v-if="createContractChoice" class="row mb-3">
                      <div class="col-12 form-group">
                        <label for="parentId" class="form-label">Typ smlouvy</label>
                        <select class="form-select" v-model="onboardingOptions.contract_type">
                          <option value="">---</option>
                          <option v-for="(contractType, index) in contractTypes" :key="index" :value="contractType.id">{{ contractType.name }}</option>
                        </select>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-12 form-group">
                        <label for="parentId" class="form-label">Generovat dokumenty (W8)</label>
                        <select class="form-select" v-model="onboardingOptions.documents_templates_w8_ids" multiple>
                          <option value="[]">---</option>
                          <option v-for="(documentTemplate, index) in documentsTemplates" :key="index" :value="documentTemplate.id">{{ documentTemplate.name }}</option>
                        </select>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-12 form-group">
                        <label for="parentId" class="form-label">Generovat dokumenty (ostatní) </label>
                        <select class="form-select" v-model="onboardingOptions.documents_templates_ids" multiple>
                          <option value="[]">---</option>
                          <option v-for="(documentTemplate, index) in documentsTemplates" :key="index" :value="documentTemplate.id">{{ documentTemplate.name }}</option>
                        </select>
                      </div>
                    </div>
                    <div class="form-group">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchStrategyChoice" v-model="onboardingOptions.strategies"/>
                        <label class="form-check-label" for="switchStrategyChoice">Volba strategií</label>
                      </div>
                      <div v-if="onboardingOptions.strategies && onboardingOptions.contract_type" class="row mb-3">
                        <span>Klientovi budou strategie přiřazeny k vytvořené smlouvě: {{ selectedContractTypeName }}</span>
                      </div>
                      <div v-if="!onboardingOptions.contract_type && onboardingOptions.strategies" class="row mb-3">
                        <span>Je potřeba zvolit vytvoření smlouvy a její typ.</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card-footer d-flex justify-content-end">
                  <button class="btn btn-primary btn-md" type="submit">Uložit</button>
                </div>
              </form>
            </div>
          </div>
<!--          Assisted onboarding-->
          <AssistedOnboardingSettings></AssistedOnboardingSettings>
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="card-header__title">
                  {{ t('settings.documents_logo') }}
                </div>
              </div>
              <div class="card-body p-4">
                <div class="row">
                  <div class="col-12 col-md-6">
                    <CustomDropzone :dropzone-options-input="{
                        url: getApiBaseUrl() + '/api/settings/documents-logo',
                        acceptedFiles: '.jpg, .jpeg, .png',
                        uploadMultiple: false,
                        paramName: 'logo',
                          init() {
                            /*this.on('addedfile', function (file, xhr, formData) {
                              intermediary.append('logo', file);});*/
                          }
                        }" :upload-success="onUploadSuccess" :upload-error="onUploadError"/>
                  </div>
                  <div v-if="documentsLogoBase64" class="col-12 col-md-6">
                    <div class="fw-bold me-3">{{ t('settings.preview_of_logo') }}</div>
                    <div class="row d-flex align-items-center">
                      <div class="col-12 col-md-6"><img class="settings-logo--preview" :src="documentsLogoBase64" alt="Logo"/></div>
                      <div class="col-12 col-md-6 d-flex justify-content-end">
                        <Button :label="t('settings.delete_logo')" @click="deleteSettingsLogo()"/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings-logo--preview {
  max-width: 100%;
  height: auto;
  margin-top: 10px;
}
</style>